import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskGauge } from "@/components/RiskGauge";
import { VerdictBadge, TierBadge, StatusText, RiskColorBadge } from "@/components/Badges";
import { toast } from "sonner";
import {
  AlertTriangle,
  FileJson,
  FileText,
  ShieldAlert,
  Sparkles,
  Gavel,
  CheckCircle2,
  XCircle,
  ArrowUpCircle,
} from "lucide-react";

function Card({ title, icon: Icon, children, testid, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-900/60 p-5 ${className}`} data-testid={testid}>
      {title && (
        <div className="mb-4 flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-blue-400" />}
          <h3 className="font-heading text-base font-semibold text-slate-200">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

export default function ScreeningResult() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [decision, setDecision] = useState("APPROVE");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get(`/screenings/${id}`).then((r) => setS(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!s) return <div className="text-slate-500">Loading case…</div>;

  const r = s.result || {};
  const enr = r.enrichment;
  const canDecide = (user?.role === "reviewer" || user?.role === "admin") && s.status === "PENDING_REVIEW";

  const exportFile = async (kind) => {
    try {
      const resp = await api.get(`/screenings/${id}/export/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `screening-${id}.${kind === "pdf" ? "pdf" : "json"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${kind.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const submitDecision = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/screenings/${id}/decision`, { decision, notes });
      setS(data);
      toast.success(`Decision recorded: ${decision}`);
      setNotes("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="screening-result-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
            {s.entity.name}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-400">
            <span className="font-mono text-xs uppercase tracking-wide">
              {enr?.entity_type || s.entity.entity_type} · {s.entity.jurisdiction || "—"}
            </span>
            <StatusText status={s.status} />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="audit-log-export-json"
            onClick={() => exportFile("json")}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
          >
            <FileJson className="h-4 w-4" /> JSON
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={() => exportFile("pdf")}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-slate-600 hover:text-white"
          >
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card testid="verdict-card" className="flex flex-col items-center justify-center text-center">
          <RiskGauge score={r.risk_score || 0} />
          <div className="mt-4 flex flex-col items-center gap-3">
            <VerdictBadge verdict={r.verdict} />
            <TierBadge tier={r.assigned_tier} />
            <p className="max-w-[240px] text-xs text-slate-500">{r.tier_label}</p>
          </div>
        </Card>

        <Card title="Hard-Override Rules" icon={ShieldAlert} testid="overrides-card" className="lg:col-span-2">
          {r.hard_overrides?.length ? (
            <div className="space-y-2">
              {r.hard_overrides.map((o, i) => (
                <div
                  key={i}
                  data-testid={`override-${i}`}
                  className="flex items-start gap-2 rounded-lg border border-amber-800/50 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                  {o}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No hard-override rules triggered — verdict driven by numeric score.</p>
          )}
          {r.enrichment_status === "failed_closed" && (
            <div className="mt-3 rounded-lg border border-red-800/50 bg-red-950/30 px-3 py-2 text-sm text-red-300">
              GPT enrichment failed schema validation — failed closed to REVIEW. {r.enrichment_error}
            </div>
          )}
        </Card>
      </div>

      <Card title="Subscription & Admin Action" icon={Gavel} testid="subscription-card">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Tier</span>
            <span className="rounded-md border border-slate-700 bg-slate-950 px-2 py-0.5 font-mono text-xs text-slate-200" data-testid="result-sub-tier">
              {r.sub_tier || "Free"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Risk color</span>
            <RiskColorBadge color={r.risk_color} score={r.risk_score} />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Admin action</span>
            <span
              data-testid="admin-action-required"
              className={r.admin_action_required ? "text-sm font-semibold text-red-400" : "text-sm text-emerald-400"}
            >
              {r.admin_action_required ? "REQUIRED" : "Not required"}
            </span>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-300" data-testid="subscription-recommendation">
          {r.recommendation}
        </p>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Deterministic Score Breakdown" icon={Gavel} testid="breakdown-card">
          <div className="space-y-2">
            {r.breakdown?.map((b, i) => (
              <div
                key={i}
                data-testid={`breakdown-${i}`}
                className="flex items-start justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2.5"
              >
                <div>
                  <div className="text-sm font-medium text-slate-200">{b.factor}</div>
                  <div className="text-xs text-slate-500">{b.detail}</div>
                </div>
                <span className="shrink-0 font-mono text-sm font-bold tabular-nums text-blue-300">
                  +{b.points}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="GPT Enrichment (schema-validated)" icon={Sparkles} testid="enrichment-card">
          {enr ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["PEP", enr.pep_status],
                  ["FATF", enr.fatf_status],
                  ["Adverse Media", enr.adverse_media_risk],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-slate-800 bg-slate-950/50 p-2.5 text-center">
                    <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
                    <div className="mt-1 text-xs font-semibold text-slate-200">{v}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Adverse media</div>
                <p className="mt-1 text-sm text-slate-300">{enr.adverse_media_summary}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Analyst narrative</div>
                <p className="mt-1 text-sm leading-relaxed text-slate-300">{enr.narrative}</p>
              </div>
              {r.crypto_flags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {r.crypto_flags.map((f) => (
                    <span key={f} className="rounded-md border border-slate-700 bg-slate-950 px-2 py-0.5 font-mono text-[10px] text-slate-300">
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-red-300">Enrichment unavailable (failed closed). Case routed to REVIEW.</p>
          )}
        </Card>
      </div>

      {r.provenance?.screened && (
        <Card title="Wallet Provenance" icon={ShieldAlert} testid="provenance-card">
          <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
            <span className="font-mono uppercase tracking-wider text-slate-500">
              {r.provenance.addresses_checked} address(es) checked · provider:{" "}
              {r.provenance.provider === "commercial" ? "commercial (TRM/Chainalysis)" : "OFAC free list + heuristics"}
            </span>
          </div>
          {r.provenance.sanctioned_hits?.length > 0 && (
            <div className="mb-2 space-y-1.5">
              {r.provenance.sanctioned_hits.map((h, i) => (
                <div key={i} data-testid={`prov-sanctioned-${i}`} className="rounded-lg border border-red-800/50 bg-red-950/30 px-3 py-2 text-sm text-red-200">
                  <span className="font-mono text-xs">{h.symbol}</span> wallet on {h.source} — linked to{" "}
                  <span className="font-semibold">{h.entity_name}</span> ({h.program || "—"})
                </div>
              ))}
            </div>
          )}
          {r.provenance.mixer_hits?.length > 0 && (
            <div className="mb-2 space-y-1.5">
              {r.provenance.mixer_hits.map((h, i) => (
                <div key={i} data-testid={`prov-mixer-${i}`} className="rounded-lg border border-amber-800/50 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                  Mixer address — {h.label}{" "}
                  <span className="font-mono text-[10px] text-slate-500">{h.address?.slice(0, 18)}…</span>
                </div>
              ))}
            </div>
          )}
          {r.provenance.sanctioned_hits?.length === 0 && r.provenance.mixer_hits?.length === 0 && (
            <p className="text-sm text-emerald-400">No sanctioned or mixer addresses detected.</p>
          )}
        </Card>
      )}

      <Card title="Sanctions Screening" icon={ShieldAlert} testid="sanctions-card">
        {r.sanctions?.matches?.length ? (
          <div className="overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Matched name</th>
                  <th className="px-3 py-2 text-left">List</th>
                  <th className="px-3 py-2 text-left">Program</th>
                  <th className="px-3 py-2 text-right">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {r.sanctions.matches.map((m, i) => (
                  <tr key={i} data-testid={`sanction-match-${i}`} className="border-t border-slate-800">
                    <td className="px-3 py-2 font-medium text-slate-200">{m.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{m.source}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{m.program || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-200">
                      {Math.round(m.confidence * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-emerald-400">No sanctions matches above the confidence threshold.</p>
        )}
      </Card>

      {s.decision && (
        <Card title="Human Review Decision" icon={Gavel} testid="decision-record-card">
          <div className="flex items-center gap-3">
            <StatusText status={s.status} />
            <span className="text-sm text-slate-300">
              <span className="font-semibold">{s.decision.decision}</span> by {s.decision.reviewer_email}
            </span>
            <span className="font-mono text-xs text-slate-500">{s.decision.decided_at}</span>
          </div>
          {s.decision.notes && <p className="mt-2 text-sm text-slate-400">“{s.decision.notes}”</p>}
        </Card>
      )}

      {canDecide && (
        <Card title="Reviewer Decision" icon={Gavel} testid="decision-panel">
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "APPROVE", icon: CheckCircle2, cls: "border-emerald-700 text-emerald-300 data-[on=true]:bg-emerald-600 data-[on=true]:text-white" },
              { v: "REJECT", icon: XCircle, cls: "border-red-700 text-red-300 data-[on=true]:bg-red-600 data-[on=true]:text-white" },
              { v: "ESCALATE", icon: ArrowUpCircle, cls: "border-fuchsia-700 text-fuchsia-300 data-[on=true]:bg-fuchsia-600 data-[on=true]:text-white" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`decision-${o.v.toLowerCase()}-btn`}
                data-on={decision === o.v}
                onClick={() => setDecision(o.v)}
                className={`flex items-center justify-center gap-2 rounded-lg border bg-slate-950 px-3 py-2.5 text-sm font-semibold transition-colors ${o.cls}`}
              >
                <o.icon className="h-4 w-4" /> {o.v}
              </button>
            ))}
          </div>
          <textarea
            data-testid="decision-notes-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Rationale / notes (recorded to the immutable audit trail)…"
            className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-blue-500"
          />
          <button
            data-testid="submit-decision-btn"
            onClick={submitDecision}
            disabled={submitting}
            className="mt-3 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            {submitting ? "Recording…" : "Record Decision"}
          </button>
        </Card>
      )}
    </div>
  );
}
